import axios from "axios";

const instance = axios.create({
  baseURL: 'https://backend.varnda.net/api',
});

// In-memory GET cache + in-flight request dedupe.
// Reduces redundant traffic when the same reference data
// (e.g. /governorates, /cities, /categories) is requested
// multiple times across components or page navigations.

const DEFAULT_TTL_MS = 5 * 60 * 1000;

// Per-endpoint TTL overrides (regex matched against URL).
const TTL_RULES = [
  { match: /^\/governorates(\?|\/|$)/, ttl: 15 * 60 * 1000 },
  { match: /^\/getallcategories/, ttl: 15 * 60 * 1000 },
  { match: /^\/getAllGovernoratesForHomepage/, ttl: 10 * 60 * 1000 },
  { match: /^\/getFilters/, ttl: 10 * 60 * 1000 },
  { match: /^\/getAd\//, ttl: 1 * 60 * 1000 },
  { match: /^\/get-ad-comments/, ttl: 30 * 1000 },
  { match: /^\/getlatest/, ttl: 2 * 60 * 1000 },
];

// Endpoints that must never be cached (user-specific, auth, or mutating reads).
const NO_CACHE_PATTERNS = [
  /^\/login/,
  /^\/logout/,
  /^\/register/,
  /^\/forgot-password/,
  /^\/reset-password/,
  /^\/profile/,
  /^\/myprofile/,
  /^\/getProfile/,
  /^\/getMyAds/,
  /^\/getFavorite/,
  /^\/google-auth/,
];

const cache = new Map();
const inFlight = new Map();

const ttlFor = (url) => {
  for (const rule of TTL_RULES) if (rule.match.test(url)) return rule.ttl;
  return DEFAULT_TTL_MS;
};

const isCacheable = (url, config) => {
  if (config?.noCache) return false;
  return !NO_CACHE_PATTERNS.some((pat) => pat.test(url));
};

const buildKey = (url, config) => {
  const params = config?.params ? JSON.stringify(config.params) : "";
  return `${url}::${params}`;
};

const originalGet = instance.get.bind(instance);

instance.get = function cachedGet(url, config) {
  if (!isCacheable(url, config)) {
    return originalGet(url, config);
  }

  const key = buildKey(url, config);

  const cached = cache.get(key);
  if (cached && cached.expiresAt > Date.now()) {
    return Promise.resolve(cached.response);
  }

  if (inFlight.has(key)) {
    return inFlight.get(key);
  }

  const promise = originalGet(url, config)
    .then((response) => {
      cache.set(key, {
        response,
        expiresAt: Date.now() + ttlFor(url),
      });
      inFlight.delete(key);
      return response;
    })
    .catch((err) => {
      inFlight.delete(key);
      throw err;
    });

  inFlight.set(key, promise);
  return promise;
};

// Mutations bust the cache so subsequent reads see fresh data.
instance.interceptors.response.use((response) => {
  const method = response.config?.method?.toLowerCase();
  if (method && method !== "get" && response.status >= 200 && response.status < 300) {
    cache.clear();
  }
  return response;
});

export const clearApiCache = () => {
  cache.clear();
  inFlight.clear();
};

export default instance;
